import { TFunction } from 'next-i18next'

import { IconName } from '~/components/Icons/types'
import { SessionStatus, Session, TutorBooking, VideoChatBooking, ProductCategory } from '~/types/tutoring-api'
import { TimeslotNew } from '~/types/booking'
import { last } from '~/utils/helpers'

export function generateTutorBookingLabels(
  bookings: Array<TutorBooking | VideoChatBooking>,
  t: TFunction<Array<'studentPanel' | 'generics'>>,
): string[] {
  return bookings
    .filter((elem): elem is TutorBooking => elem.type === ProductCategory.TUTORING)
    .map((elem) => `${elem.totalHours} ${elem.totalHours > 1 ? t('generics:hours') : t('generics:hour')}`)
}

export function getSessionWithStatus(sessions: Session[], status: SessionStatus): number {
  return sessions.filter((session) => session.status === status).length
}

export function getSessionStatusData(
  completedHours: number,
  bookedHours: number,
  cancelledHours: number,
  lateCancelledHours: number,
  noShowCancelledHours: number,
  totalHours: number,
  status: SessionStatus,
): string | number {
  switch (status) {
    case SessionStatus.PENDING:
      return formatHours(bookedHours)

    case SessionStatus.CANCELLED:
      return formatHours(cancelledHours)

    case SessionStatus.CANCELLED_NO_SHOW:
      return formatHours(noShowCancelledHours)

    case SessionStatus.CANCELLED_LATE:
      return formatHours(lateCancelledHours)

    case SessionStatus.COMPLETED:
      return `${formatHours(completedHours)}/${formatHours(totalHours)}`

    default:
      return ''
  }
}

export function getSessionStatusColor(sessionStatus: SessionStatus): string {
  switch (sessionStatus) {
    case SessionStatus.PENDING:
      return 'text-gray-light-fade'

    case SessionStatus.COMPLETED:
      return 'text-green-status'

    case SessionStatus.CANCELLED:
    case SessionStatus.CANCELLED_LATE:
    case SessionStatus.CANCELLED_NO_SHOW:
      return 'text-red'

    default:
      return ''
  }
}

export function getSessionStatusIcon(sessionStatus: SessionStatus): IconName {
  switch (sessionStatus) {
    case SessionStatus.PENDING:
      return 'status-pending'

    case SessionStatus.COMPLETED:
      return 'status-completed'

    case SessionStatus.CANCELLED:
      return 'status-cancelled'

    case SessionStatus.CANCELLED_LATE:
      return 'status-less-24'

    case SessionStatus.CANCELLED_NO_SHOW:
      return 'status-no-show'

    default:
      return 'times'
  }
}

export function getNextSession(sessions: Session[] | undefined): Session | undefined {
  if (sessions && sessions.length > 0) {
    const sessionsCopy = [...sessions.filter((elem) => elem.status === SessionStatus.PENDING)]
    // eslint-disable-next-line fp/no-mutating-methods
    return sessionsCopy.sort((a, b) => {
      return new Date(a.startTime).valueOf() - new Date(b.startTime).valueOf()
    })[0]
  }
}

export function chunkSubsequentTimeslots(timeslots: TimeslotNew[]): TimeslotNew[] {
  // eslint-disable-next-line fp/no-mutating-methods
  return [...timeslots]
    .sort((a, b) => (a.startTime > b.startTime ? 1 : -1))
    .reduce((acc, currValue) => {
      const lastItem = last(acc)

      if (!lastItem || lastItem.endTime !== currValue.startTime) {
        return [...acc, { ...currValue }]
      } else {
        return [...acc.slice(0, acc.length - 1), { ...lastItem, endTime: currValue.endTime }]
      }
    }, [] as TimeslotNew[])
}

export function formatHours(hours?: number): string {
  if (typeof hours === 'number') {
    return parseFloat(hours.toFixed(2)).toString()
  } else {
    return 'N/A'
  }
}

export function getStripeProductDashboardUrl(productId: string): string {
  const productUrl =
    process.env.NEXT_PUBLIC_ENVIRONMENT === 'prod' || process.env.NEXT_PUBLIC_ENVIRONMENT === 'beta'
      ? '/products'
      : '/test/products'
  return `https://dashboard.stripe.com${productUrl}/${productId}`
}

export function getStripePriceDashboardUrl(priceId: string): string {
  const priceUrl =
    process.env.NEXT_PUBLIC_ENVIRONMENT === 'prod' || process.env.NEXT_PUBLIC_ENVIRONMENT === 'beta'
      ? '/prices'
      : '/test/prices'
  return `https://dashboard.stripe.com${priceUrl}/${priceId}`
}

export function extractGoogleGeoLocation(components: any[]): {
  city: string
  zip: string
  region: string
  country: string
} {
  let city = ''
  let zip = ''
  let region = ''
  let country = ''

  components.forEach((component) => {
    const componentType = component.types[0]

    switch (componentType) {
      case 'locality':
        city = component.long_name
        break
      case 'postal_code':
        zip = component.long_name
        break
      case 'administrative_area_level_1':
        region = component.long_name
        break
      case 'country':
        country = component.long_name
        break
    }
  })
  return { city, zip, region, country }
}
